import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Flame, RotateCcw, BookMarked, Clapperboard, Compass, Loader2, Check,
  Bell, ChevronDown, Target, BarChart3, BookOpen, Clock, Mic, ChevronRight } from "lucide-react";
import BottomNav from "@/components/BottomNav";
import NotificationCenter from "@/components/NotificationCenter";
import client from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";

const HERO = "https://static.prod-images.emergentagent.com/jobs/3085e34f-2862-4361-89ba-1e9784a7f039/images/270ee094db0aa7fafbf74d6a971aa2c5962ddfcdf61516afdc2aa7ac33374370.jpeg";
const MARK = {
  WebkitMaskImage: "url(/melletic-mark.png)", maskImage: "url(/melletic-mark.png)",
  WebkitMaskSize: "contain", maskSize: "contain", WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
  WebkitMaskPosition: "center", maskPosition: "center",
  background: "linear-gradient(135deg,#bcddff 0%,#6aa6f7 30%,#2f6fe0 62%,#17368f 100%)",
  filter: "drop-shadow(0 3px 7px rgba(23,42,110,0.3))",
};
const LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];
const LEVEL_LABEL = { A1: "Beginner", A2: "Elementary", B1: "Intermediate", B2: "Upper-Int.", C1: "Advanced", C2: "Mastery" };
const LANG_NAME = { de: "German", en: "English", fr: "French", it: "Italian", es: "Spanish" };

export default function Home() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useLang();
  const [state, setState] = useState(null);
  const [extra, setExtra] = useState({ learned: 0, due: 0, story: null, life: 0, rec: [] });
  const [pp, setPp] = useState(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const loadUnread = () => client.get("/notifications").then((r) => setUnread(r.data?.unread || 0)).catch(() => {});
  useEffect(() => { loadUnread(); }, []);

  useEffect(() => {
    const load = () => {
      client.get("/learn/state").then(({ data }) => {
        if (!data.active_language) { nav("/choose-language"); return; }
        setState(data);
      }).catch(() => {});
      client.get("/learn/passport").then((r) => setPp(r.data?.passports?.[0] || null)).catch(() => {});
      Promise.all([
        client.get("/learn/words").then((r) => r.data).catch(() => []),
        client.get("/learn/stories").then((r) => r.data).catch(() => []),
        client.get("/learn/life").then((r) => r.data).catch(() => []),
      ]).then(([words, stories, life]) => {
        const learned = words.filter((w) => w.status === "learned" || w.status === "strong").length;
        const due = words.filter((w) => (w.status || "learning") === "review").length;
        const story = stories.find((s) => s.unlocked && s.status !== "completed") || null;
        const lifeCount = life.filter((m) => m.unlocked && m.status !== "completed").length;
        const rec = [...stories.filter((s) => s.unlocked).map((s) => ({ ...s, kind: "story", to: `/story/${s.id}` })),
          ...life.filter((m) => m.unlocked).map((m) => ({ ...m, kind: "life", to: `/life/${m.id}` }))]
          .filter((x) => x.cover).slice(0, 6);
        setExtra({ learned, due, story, life: lifeCount, rec });
      });
    };
    load();
    const onRefresh = () => load();
    const onFocus = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("mel:progress-refresh", onRefresh);
    document.addEventListener("visibilitychange", onFocus);
    return () => { window.removeEventListener("mel:progress-refresh", onRefresh); document.removeEventListener("visibilitychange", onFocus); };
  }, [nav]);

  if (!state) return <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center"><Loader2 className="animate-spin text-[#2563EB]" /></div>;

  const c = state.continue;
  const name = (user?.name || "").split(" ")[0] || "there";
  const hour = new Date().getHours();
  const greet = hour < 12 ? t("morning") : hour < 18 ? t("afternoon") : t("evening");
  const langPill = state.languages.find((l) => l.code === state.active_language) || {};
  const curLevel = langPill.level || "A1";
  const modPct = c ? Math.round(((c.lesson_n - 1) / Math.max(1, c.module_lessons)) * 100) : 0;
  const todayPct = Math.min(100, Math.round((state.today_minutes / Math.max(1, state.daily_goal)) * 100));
  const fmt = (m) => { const x = Math.round(m || 0); return x >= 60 ? `${Math.floor(x / 60)}h ${x % 60}m` : `${x}m`; };

  const day = [
    { id: "review", label: t("home_quick_review"), sub: t("home_quick_review_sub"), icon: RotateCcw, tint: "#2563EB", to: "/review" },
    { id: "words", label: t("home_my_words"), sub: t("home_my_words_sub"), icon: BookMarked, tint: "#8B5CF6", to: "/words" },
    { id: "story", label: t("home_story"), sub: t("home_story_sub"), icon: Clapperboard, tint: "#F59E0B", to: "/stories" },
    { id: "life", label: t("home_life"), sub: t("home_life_sub"), icon: Compass, tint: "#22C55E", to: "/life" },
  ];
  const stats = [
    { label: t("home_lessons_completed"), val: pp?.lessons_completed ?? 0, icon: BarChart3, tint: "#2563EB" },
    { label: t("home_words_learned"), val: pp?.words ?? extra.learned, icon: BookOpen, tint: "#22C55E" },
    { label: t("home_learning_time"), val: fmt(pp?.learning_minutes), icon: Clock, tint: "#8B5CF6" },
    { label: t("home_speaking_time"), val: fmt(pp?.speaking_minutes), icon: Mic, tint: "#EC4899" },
  ];
  const curIdx = LEVELS.indexOf(curLevel);

  return (
    <div className="min-h-screen w-full bg-[#0B1220] flex justify-center">
      <div className="relative w-full max-w-[460px] min-h-screen bg-[#F8FAFC]">
        <div className="pb-28">
          {/* ===== Cinematic hero header ===== */}
          <div className="relative">
            <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "center 40%" }} />
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg,rgba(248,250,252,0.98) 0%,rgba(248,250,252,0.97) 30%,rgba(248,250,252,0.86) 45%,rgba(248,250,252,0.42) 62%,rgba(248,250,252,0.55) 84%,#F8FAFC 100%)" }} />
            <div className="relative z-10 px-5" style={{ paddingTop: "calc(env(safe-area-inset-top,0px) + 36px)" }}>
              {/* top bar */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div style={{ ...MARK, width: 30, height: 30 * (243 / 490) * 1.95 }} />
                  <div className="leading-none">
                    <p className="font-heading font-extrabold text-[#0B1727]" style={{ letterSpacing: "0.2em", fontSize: 13 }}>MELLETIC</p>
                    <p className="text-[9px] font-bold mt-0.5"><span className="text-[#475569]">{t("tagline_a")} </span><span className="text-[#2563EB]">{t("tagline_b")}</span></p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setNotifOpen(true)} data-testid="home-bell" className="relative w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm"><Bell size={18} className="text-[#0F172A]" />{unread > 0 && <span data-testid="notif-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#2563EB] text-white text-[10px] font-bold flex items-center justify-center">{unread > 9 ? "9+" : unread}</span>}</button>
                </div>
              </div>

              {/* greeting */}
              <div className="mt-7">
                <p className="text-[14px] font-semibold text-[#475569]">{greet},</p>
                <h1 className="font-heading text-[28px] leading-tight font-extrabold text-[#0F172A]" data-testid="home-greeting" style={{ textShadow: "0 1px 12px rgba(248,250,252,0.9)" }}>{name} 👋</h1>
                <p className="text-[#334155] mt-1 text-[14px] font-medium max-w-[250px]">{t("home_tagline")}</p>
              </div>

              {/* language pill */}
              <button onClick={() => nav("/settings")} data-testid="home-lang-pill"
                className="mt-4 mb-24 inline-flex items-center gap-2.5 bg-white rounded-2xl pl-3 pr-3 py-2.5 shadow-[0_10px_28px_-16px_rgba(15,23,42,0.6)] border border-white/70">
                <span className="text-xl leading-none">{langPill.flag}</span>
                <span className="text-[15px] font-extrabold text-[#0F172A]">{t(`lang_${langPill.code}`)}</span>
                <ChevronDown size={15} className="text-[#94A3B8]" />
                <span className="w-px h-6 bg-slate-200 mx-0.5" />
                <span className="text-left leading-none"><span className="block text-[15px] font-extrabold text-[#2563EB]">{curLevel}</span><span className="block text-[10px] text-[#64748B] mt-0.5">{t(`cefr_${curLevel}`)}</span></span>
              </button>
            </div>
          </div>

          {/* ===== Continue Learning (overlaps hero) ===== */}
          <div className="px-5 -mt-16 relative z-10">
            {c ? (
              <button onClick={() => nav(`/lesson/${c.lesson_id}`)} data-testid="continue-card"
                className="w-full text-left rounded-[24px] bg-white border border-slate-200/70 shadow-[0_26px_54px_-26px_rgba(15,23,42,0.55)] p-3.5 flex gap-3.5 items-center active:scale-[0.99] transition-transform">
                <img src={c.module_cover || c.cover} alt="" className="w-24 h-24 rounded-2xl object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-extrabold text-[#2563EB]">{t("home_continue_learning")}</p>
                  <h2 className="font-heading text-lg font-extrabold text-[#0F172A] leading-tight line-clamp-2">{c.lesson_title}</h2>
                  <p className="text-[12px] text-[#475569] truncate">{t("home_module")} {c.module_n} · {c.module_title}</p>
                  <div className="mt-2 h-1.5 rounded-full bg-[#EDF2F7] overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-[#2563EB] to-[#3B82F6]" style={{ width: `${modPct}%` }} /></div>
                  <div className="flex items-center justify-between mt-1"><span className="text-[11px] text-[#64748B]">{t("home_lesson_of", { n: c.lesson_n, t: c.module_lessons })}</span><span className="text-[13px] font-extrabold text-[#0F172A]">{modPct}%</span></div>
                </div>
                <span className="w-11 h-11 rounded-full bg-[#EAF1FE] flex items-center justify-center shrink-0"><ChevronRight size={22} className="text-[#2563EB]" /></span>
              </button>
            ) : (
              <div className="rounded-[24px] bg-white border border-slate-200/70 p-6 text-center shadow-lg" data-testid="home-alldone">
                <p className="font-heading text-lg font-extrabold text-[#0F172A]">You're all caught up! 🎉</p>
                <p className="text-[#475569] text-sm mt-1">Try a Review or a LIFE mission to keep your streak.</p>
              </div>
            )}
          </div>

          {/* ===== Streak + Daily goal ===== */}
          <div className="px-5 mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-2xl bg-white border border-slate-200/70 p-4 flex items-center gap-3" data-testid="streak-card">
              <span className="w-11 h-11 rounded-2xl bg-[#FEF3C7] flex items-center justify-center"><Flame className="text-[#F59E0B]" size={20} /></span>
              <div><p className="font-heading text-xl font-extrabold text-[#0F172A] leading-none">{state.streak}</p><p className="text-[12px] text-[#475569] mt-0.5">{t("home_day_streak")}</p></div>
            </div>
            <div className="rounded-2xl bg-white border border-slate-200/70 p-4" data-testid="today-goal">
              <div className="flex items-center gap-2.5">
                <span className="w-11 h-11 rounded-2xl bg-[#E0F2FE] flex items-center justify-center shrink-0"><Target className="text-[#2563EB]" size={20} /></span>
                <div className="min-w-0"><p className="font-heading text-[15px] font-extrabold text-[#0F172A] leading-none flex items-center gap-1">{Math.round(state.today_minutes)} / {state.daily_goal} min {todayPct >= 100 && <Check size={14} className="text-[#22C55E]" />}</p><p className="text-[12px] text-[#475569] mt-0.5">{t("home_daily_goal")}</p></div>
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-[#EDF2F7] overflow-hidden"><div className="h-full rounded-full bg-[#2563EB]" style={{ width: `${todayPct}%` }} /></div>
            </div>
          </div>

          {/* ===== Continue your day ===== */}
          <div className="px-5 mt-6">
            <div className="flex items-center justify-between mb-3"><h3 className="font-heading text-lg font-extrabold text-[#0F172A]">{t("home_continue_day")}</h3><button onClick={() => nav("/learn")} className="text-[13px] font-bold text-[#2563EB] flex items-center gap-0.5" data-testid="day-seeall">{t("home_see_all")} <ChevronRight size={15} /></button></div>
            <div className="grid grid-cols-2 gap-3">
              {day.map((q) => (
                <button key={q.id} onClick={() => nav(q.to)} data-testid={`quick-${q.id}`} className="rounded-2xl border border-slate-200/70 p-4 text-left active:scale-[0.98] transition-transform" style={{ background: `${q.tint}0D` }}>
                  <span className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: `${q.tint}1F`, color: q.tint }}><q.icon size={20} /></span>
                  <p className="font-heading font-extrabold text-[#0F172A] text-[15px] mt-3">{q.label}</p>
                  <p className="text-[12px] text-[#475569] leading-tight mt-0.5">{q.sub}</p>
                </button>
              ))}
            </div>
          </div>

          {/* ===== Your Language Journey ===== */}
          <div className="px-5 mt-6">
            <div className="flex items-center justify-between mb-3"><h3 className="font-heading text-lg font-extrabold text-[#0F172A]">{t("home_journey_title")}</h3><button onClick={() => nav("/learn")} className="text-[13px] font-bold text-[#2563EB] flex items-center gap-0.5" data-testid="journey-viewall">{t("home_view_all")} <ChevronRight size={15} /></button></div>
            <div className="flex items-center justify-between px-1">
              {LEVELS.map((lv, i) => {
                const done = i < curIdx, current = i === curIdx;
                return (
                  <React.Fragment key={lv}>
                    <div className="flex flex-col items-center gap-1.5">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-extrabold ${done ? "bg-[#2563EB] text-white" : current ? "bg-white ring-[3px] ring-[#2563EB] text-[#2563EB]" : "bg-slate-200 text-slate-400"}`}>{done ? <Check size={15} /> : current ? <span className="w-2.5 h-2.5 rounded-full bg-[#2563EB]" /> : ""}</span>
                      <span className={`text-[11px] font-bold ${done || current ? "text-[#0F172A]" : "text-slate-400"}`}>{lv}</span>
                    </div>
                    {i < LEVELS.length - 1 && <div className={`flex-1 h-0.5 mx-1 -mt-4 ${i < curIdx ? "bg-[#2563EB]" : "bg-slate-200"}`} />}
                  </React.Fragment>
                );
              })}
            </div>
            <div className="grid grid-cols-4 gap-2.5 mt-4">
              {stats.map((s) => (
                <div key={s.label} className="rounded-2xl bg-white border border-slate-200/70 p-2.5 text-center" data-testid={`home-stat-${s.label.split(" ")[0].toLowerCase()}`}>
                  <s.icon size={16} className="mx-auto" style={{ color: s.tint }} />
                  <p className="font-heading text-base font-extrabold text-[#0F172A] mt-1 leading-none">{s.val}</p>
                  <p className="text-[9px] text-[#64748B] leading-tight mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ===== Recommended for you ===== */}
          {extra.rec.length > 0 && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-3 px-5"><h3 className="font-heading text-lg font-extrabold text-[#0F172A]">{t("home_recommended")}</h3><button onClick={() => nav("/life")} className="text-[13px] font-bold text-[#2563EB] flex items-center gap-0.5" data-testid="rec-seeall">{t("home_see_all")} <ChevronRight size={15} /></button></div>
              <div className="flex gap-3 overflow-x-auto px-5 pb-1" style={{ scrollbarWidth: "none" }}>
                {extra.rec.map((r) => (
                  <button key={r.id} onClick={() => nav(r.to)} data-testid={`rec-${r.id}`} className="shrink-0 w-40 text-left">
                    <div className="relative h-24 rounded-2xl overflow-hidden"><img src={r.cover} alt="" className="w-full h-full object-cover" /><span className="absolute top-2 left-2 text-[9px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-full bg-white/90 text-[#2563EB]">{r.kind}</span></div>
                    <p className="font-bold text-[13px] text-[#0F172A] mt-1.5 leading-tight truncate">{r.title}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <BottomNav />
      </div>
      <NotificationCenter open={notifOpen} onClose={() => { setNotifOpen(false); loadUnread(); }} onChange={loadUnread} />
    </div>
  );
}
